import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    QuestionKinds, ResponseTypes, ALL_RESPONSE_TYPES, kindLabel, responseTypeLabel
} from '@aiinterview/shared';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, TextArea, Select, Loading, ErrorNote,
    confirmAction, toast
} from '../components/ui';
import { DataTable } from '../components/DataTable';
import { FileUpload } from '../components/FileUpload';

interface Question {
    id: number;
    kind: string;
    responseType: string;
    optionsJson: string;
    allowMultiple: boolean;
    criterionId: number | null;
    questionText: string;
    expectedAnswerHint: string | null;
    difficultyLevel: number;
    maxAnswerSeconds: number;
    imagePath: string | null;
    criterion: { id: number; name: string } | null;
}

const EMPTY = {
    id: undefined as number | undefined,
    responseType: ResponseTypes.Audio as string,
    options: [] as string[],
    allowMultiple: true,
    criterionId: '',
    questionText: '',
    expectedAnswerHint: '',
    difficultyLevel: '2',
    maxAnswerSeconds: '0',
    imagePath: null as string | null
};

const DIFFICULTY = [['1', 'Easy'], ['2', 'Medium'], ['3', 'Hard']] as const;

/**
 * The three sections of the bank. Interview questions are scored against a criterion; survey and
 * listening questions are not scored at all, so those two fields disappear rather than sitting
 * there asking for something that would be ignored.
 */
const SECTIONS = [
    QuestionKinds.Interview, QuestionKinds.Survey, QuestionKinds.Listening
] as const;

/** Options are stored as JSON on the row; the editor works in an array and converts on load. */
const parseOptions = (json: string | null | undefined): string[] => {
    try {
        const parsed = JSON.parse(json || '[]');
        return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
    } catch {
        return [];
    }
};

export function Questions() {
    const [kind, setKind] = useState<string>(QuestionKinds.Interview);
    const [form, setForm] = useState(EMPTY);
    const [filter, setFilter] = useState('');
    const [error, setError] = useState<ApiError | null>(null);
    const queryClient = useQueryClient();

    const criteria = useQuery({
        queryKey: ['criteria'],
        queryFn: () => api.get<{ id: number; name: string }[]>('/api/admin/criteria')
    });

    const scored = kind === QuestionKinds.Interview;

    /**
     * Only a survey question needs a response type. An interview is always spoken, and a listening
     * session is spoken with the employee choosing audio or video for the whole thing - so offering
     * a per-question choice there would be a control that does nothing.
     */
    const picksResponseType = kind === QuestionKinds.Survey;
    const isChoice = picksResponseType && form.responseType === ResponseTypes.Checklist;

    const questions = useQuery({
        queryKey: ['questions', kind, filter],
        queryFn: () => api.get<Question[]>(
            `/api/admin/questions?kind=${kind}${filter ? `&criterionId=${filter}` : ''}`)
    });

    const save = useMutation({
        mutationFn: () => api.post('/api/admin/questions', {
            ...form,
            kind,
            // A section that does not pick one still has to send something the server accepts, and
            // spoken is what every question in the bank was before response types existed.
            responseType: picksResponseType ? form.responseType : ResponseTypes.Audio,
            options: isChoice ? form.options.map((o) => o.trim()).filter(Boolean) : [],
            criterionId: form.criterionId ? Number(form.criterionId) : null,
            difficultyLevel: Number(form.difficultyLevel),
            maxAnswerSeconds: Number(form.maxAnswerSeconds)
        }),
        onSuccess: async () => {
            toast('Question saved.');
            setForm(EMPTY);
            setError(null);
            await queryClient.invalidateQueries({ queryKey: ['questions'] });
        },
        onError: (caught: ApiError) => setError(caught)
    });

    const remove = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/questions/${id}/delete`),
        onSuccess: async () => {
            toast('Question retired.');
            await queryClient.invalidateQueries({ queryKey: ['questions'] });
        },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    const clearTranslations = useMutation({
        mutationFn: (id: number) => api.post<number>(`/api/admin/questions/${id}/clear-translations`),
        onSuccess: (count) => toast(`Cleared ${count} cached translation(s).`),
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    return (
        // Stacked, matching Interview formats. In the two-column grid the question text wrapped to one
        // or two words per line and the editor never had room.
        <div className="space-y-6">
            <Card
                heading
                title="Questions"
                lede={scored
                    ? 'Curated questions. Interview formats either ask these directly or use them to ground AI-generated ones.'
                    : `Questions a ${kindLabel(kind).toLowerCase()} asks. Nothing here is scored, so there is no criterion and no rubric.`}
            >
                <div className="mb-4 flex flex-wrap gap-4">
                    <div className="max-w-xs">
                        <Field label="Section">
                            <Select
                                value={kind}
                                onChange={(e) => {
                                    // The open editor belongs to the section being left, and a
                                    // half-typed survey question must not be saved into the
                                    // interview bank on the next Save.
                                    setKind(e.target.value);
                                    setFilter('');
                                    setForm(EMPTY);
                                    setError(null);
                                }}
                            >
                                {SECTIONS.map((section) => (
                                    <option key={section} value={section}>
                                        {kindLabel(section)}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                    </div>

                    {scored && (
                        <div className="max-w-xs">
                            <Field label="Filter by criterion">
                                <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
                                    <option value="">All criteria</option>
                                    {(criteria.data ?? []).map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </Select>
                            </Field>
                        </div>
                    )}
                </div>

                {questions.isLoading ? <Loading /> : (
                    <DataTable
                        rows={questions.data ?? []}
                        rowKey={(row) => row.id}
                        empty="No questions yet."
                        columns={[
                            { header: 'Question', sortBy: (row) => row.questionText, cell: (row) => row.questionText },
                            ...(scored
                                ? [{
                                    header: 'Criterion',
                                    sortBy: (row: Question) => row.criterion?.name ?? null,
                                    cell: (row: Question) => row.criterion?.name ?? '—'
                                }]
                                : []),
                            ...(picksResponseType
                                ? [{
                                    header: 'How answered',
                                    sortBy: (row: Question) => row.responseType,
                                    cell: (row: Question) => (
                                        <span>
                                            {responseTypeLabel(row.responseType)}
                                            {row.responseType === ResponseTypes.Checklist && (
                                                <span className="ml-2 text-xs text-slate-500">
                                                    {parseOptions(row.optionsJson).length} options
                                                    {row.allowMultiple ? '' : ', one answer'}
                                                </span>
                                            )}
                                        </span>
                                    )
                                }]
                                : []),
                            {
                                header: 'Difficulty', sortBy: (row) => row.difficultyLevel,
                                cell: (row) => DIFFICULTY.find(
                                    ([value]) => Number(value) === row.difficultyLevel)?.[1] ?? row.difficultyLevel
                            },
                            {
                                header: '',
                                className: 'text-right',
                                cell: (row) => (
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            variant="secondary"
                                            onClick={() => setForm({
                                                id: row.id,
                                                responseType: row.responseType,
                                                options: parseOptions(row.optionsJson),
                                                allowMultiple: row.allowMultiple,
                                                criterionId: row.criterionId ? String(row.criterionId) : '',
                                                questionText: row.questionText,
                                                expectedAnswerHint: row.expectedAnswerHint ?? '',
                                                difficultyLevel: String(row.difficultyLevel),
                                                maxAnswerSeconds: String(row.maxAnswerSeconds),
                                                imagePath: row.imagePath
                                            })}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={async () => {
                                                const confirmed = await confirmAction({
                                                    title: 'Remove this question?',
                                                    confirmText: 'Retire question',
                                                    danger: true
                                                });
                                                if (confirmed) remove.mutate(row.id);
                                            }}
                                        >
                                            Retire
                                        </Button>
                                    </div>
                                )
                            }
                        ]}
                    />
                )}
            </Card>

            <Card title={form.id
                ? `Edit ${kindLabel(kind).toLowerCase()} question`
                : `Add a ${kindLabel(kind).toLowerCase()} question`}
                lede={kind === QuestionKinds.Listening
                    ? 'Listening questions are always answered out loud. The employee chooses '
                      + 'whether that is audio or on camera when they start.'
                    : undefined}
            >
                <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
                    {error && !error.errors && <ErrorNote message={error.message} />}

                    {scored && (
                        <Field label="Criterion" error={error?.fieldError('criterionId')}>
                            <Select
                                value={form.criterionId}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, criterionId: e.target.value }))}
                                invalid={!!error?.fieldError('criterionId')}
                            >
                                <option value="">Choose a criterion…</option>
                                {(criteria.data ?? []).map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </Select>
                        </Field>
                    )}

                    {picksResponseType && (
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field
                                label="How it is answered"
                                hint="Only used when this question is in a questionnaire."
                            >
                                <Select
                                    value={form.responseType}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, responseType: e.target.value }))}
                                >
                                    {ALL_RESPONSE_TYPES.map((type) => (
                                        <option key={type} value={type}>
                                            {responseTypeLabel(type)}
                                        </option>
                                    ))}
                                </Select>
                            </Field>

                            {isChoice && (
                                <Field label="How many they may pick">
                                    <Select
                                        value={form.allowMultiple ? 'many' : 'one'}
                                        onChange={(e) => setForm((f) => ({
                                            ...f, allowMultiple: e.target.value === 'many'
                                        }))}
                                    >
                                        <option value="one">Only one answer (radio)</option>
                                        <option value="many">Any number of answers (checklist)</option>
                                    </Select>
                                </Field>
                            )}
                        </div>
                    )}

                    {isChoice && (
                        <Field
                            label="Options to choose from"
                            error={error?.fieldError('options')}
                            hint="In the order they will be shown. At least two."
                        >
                            <div className="space-y-2">
                                {form.options.map((option, index) => (
                                    <div key={index} className="flex gap-2">
                                        <TextInput
                                            value={option}
                                            onChange={(e) => setForm((f) => ({
                                                ...f,
                                                options: f.options.map(
                                                    (o, i) => i === index ? e.target.value : o)
                                            }))}
                                        />
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={() => setForm((f) => ({
                                                ...f,
                                                options: f.options.filter((_, i) => i !== index)
                                            }))}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() =>
                                        setForm((f) => ({ ...f, options: [...f.options, ''] }))}
                                >
                                    Add an option
                                </Button>
                            </div>
                        </Field>
                    )}

                    <Field label="Question" error={error?.fieldError('questionText')}>
                        <TextArea
                            value={form.questionText}
                            onChange={(e) => setForm((f) => ({ ...f, questionText: e.target.value }))}
                            invalid={!!error?.fieldError('questionText')}
                            rows={3}
                        />
                    </Field>

                    {scored && (
                        <Field
                            label="What a strong answer covers"
                            hint="Used as the scoring rubric. Never shown to the candidate."
                        >
                            <TextArea
                                value={form.expectedAnswerHint}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, expectedAnswerHint: e.target.value }))}
                                rows={4}
                            />
                        </Field>
                    )}

                    <Field
                        label="Picture (optional)"
                        hint="Shown to the candidate beside this question while it is asked. PNG, JPG, GIF or WEBP, up to 2MB."
                        error={error?.fieldError('imagePath')}
                    >
                        <FileUpload
                            purpose="question-image"
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            currentName={form.imagePath ? 'Picture attached' : null}
                            onUploaded={(file) => setForm((f) => ({ ...f, imagePath: file.path }))}
                            onCleared={() => setForm((f) => ({ ...f, imagePath: null }))}
                        />

                        {form.imagePath && (
                            <img
                                src={`/api/assets/${form.imagePath.split('/').pop()}`}
                                alt=""
                                className="mt-2 max-h-32 rounded border"
                                style={{ borderColor: 'var(--app-border)' }}
                            />
                        )}
                    </Field>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Difficulty">
                            <Select
                                value={form.difficultyLevel}
                                onChange={(e) => setForm((f) => ({ ...f, difficultyLevel: e.target.value }))}
                            >
                                {DIFFICULTY.map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </Select>
                        </Field>

                        <Field label="Answer time" hint="Seconds. 0 uses the format default.">
                            <TextInput
                                type="number"
                                min="0"
                                value={form.maxAnswerSeconds}
                                onChange={(e) => setForm((f) => ({ ...f, maxAnswerSeconds: e.target.value }))}
                            />
                        </Field>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button type="submit" busy={save.isPending}>Save</Button>
                        {form.id && (
                            <>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => { setForm(EMPTY); setError(null); }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    busy={clearTranslations.isPending}
                                    onClick={() => clearTranslations.mutate(form.id!)}
                                >
                                    Clear translations
                                </Button>
                            </>
                        )}
                    </div>

                    {form.id && (
                        <p className="text-xs text-slate-500">
                            Editing the wording leaves any cached translations out of date. Clear them
                            so the next candidate gets a fresh one.
                        </p>
                    )}
                </form>
            </Card>
        </div>
    );
}
